import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { LogOut, Home, Plus, Users, Calendar, Trash2, Eye, History } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminEvents() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showRegistrations, setShowRegistrations] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [tab, setTab] = useState('upcoming'); // 'upcoming' | 'past'
  const [form, setForm] = useState({
    title: '', slug: '', description: '', short_description: '',
    event_type: 'webinar', event_date: '', event_time: '',
    location: '', is_virtual: true, capacity: '', price: ''
  });

  useEffect(() => { fetchEvents(); }, []);

  // Today as YYYY-MM-DD for filtering (local time, matches event_date storage)
  const todayIso = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const upcomingEvents = useMemo(
    () => events.filter((e) => (e.event_date || '') >= todayIso).sort((a, b) => (a.event_date || '').localeCompare(b.event_date || '')),
    [events, todayIso]
  );
  const pastEvents = useMemo(
    () => events.filter((e) => (e.event_date || '') < todayIso).sort((a, b) => (b.event_date || '').localeCompare(a.event_date || '')),
    [events, todayIso]
  );
  const visibleEvents = tab === 'upcoming' ? upcomingEvents : pastEvents;

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/events`, { withCredentials: true });
      setEvents(res.data);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/events`, {
        ...form,
        slug: form.slug || form.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
        capacity: form.capacity ? parseInt(form.capacity) : null
      }, { withCredentials: true });
      toast.success('Event created');
      setShowCreate(false);
      setForm({ title: '', slug: '', description: '', short_description: '', event_type: 'webinar', event_date: '', event_time: '', location: '', is_virtual: true, capacity: '', price: '' });
      fetchEvents();
    } catch { toast.error('Failed to create event'); }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/events/${eventId}`, { withCredentials: true });
      toast.success('Event deleted');
      fetchEvents();
    } catch { toast.error('Failed to delete event'); }
  };

  const viewRegistrations = async (eventId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/events/${eventId}/registrations`, { withCredentials: true });
      setRegistrations(res.data);
      setShowRegistrations(eventId);
    } catch { toast.error('Failed to load registrations'); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <AdminLayout title="Events"><div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#0A0F1C]" data-testid="events-admin-title">Events ({events.length})</h1>
          <Button onClick={() => setShowCreate(true)} className="btn-primary-blue flex items-center gap-2" data-testid="create-event-btn">
            <Plus size={16} /> Create Event
          </Button>
        </div>

        <div className="space-y-4">
          {/* Upcoming / Past tab switch */}
          <div className="flex items-center gap-2 border-b border-[#E2E8F0] pb-2" data-testid="events-tabs">
            <button
              onClick={() => setTab('upcoming')}
              className={`px-3 py-1.5 text-sm font-bold rounded-t-md transition ${tab === 'upcoming' ? 'bg-[#1D4ED8] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}`}
              data-testid="events-tab-upcoming"
            >
              <Calendar size={13} className="inline mr-1.5 -mt-0.5" />
              Upcoming ({upcomingEvents.length})
            </button>
            <button
              onClick={() => setTab('past')}
              className={`px-3 py-1.5 text-sm font-bold rounded-t-md transition ${tab === 'past' ? 'bg-[#475569] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}`}
              data-testid="events-tab-past"
            >
              <History size={13} className="inline mr-1.5 -mt-0.5" />
              Past ({pastEvents.length})
            </button>
            <span className="ml-auto text-xs text-[#94A3B8]">
              {tab === 'past' ? 'Hidden from public site - kept for analytics' : 'Visible on public Events page'}
            </span>
          </div>

          {visibleEvents.length === 0 && (
            <div className="text-center py-12 text-[#94A3B8] text-sm bg-white border border-[#E2E8F0] rounded-lg" data-testid="events-empty">
              {tab === 'upcoming' ? 'No upcoming events. Click "Create Event" to add one.' : 'No past events yet.'}
            </div>
          )}
          {visibleEvents.map((evt, i) => (
            <div
              key={evt.event_id}
              className={`bg-white p-6 border border-[#E2E8F0] rounded-lg ${tab === 'past' ? 'opacity-70' : ''}`}
              data-testid={`admin-event-${i}`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-[#0A0F1C]">{evt.title}</h3>
                    <span className="text-xs px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">{evt.event_type}</span>
                    {!evt.is_active && <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Inactive</span>}
                    {tab === 'past' && <span className="text-xs px-2 py-0.5 rounded bg-[#475569] text-white" data-testid={`past-badge-${i}`}>Past</span>}
                  </div>
                  <p className="text-sm text-[#475569]">{evt.short_description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-[#94A3B8]">
                    <span className="flex items-center gap-1"><Calendar size={12} />{evt.event_date}</span>
                    {evt.event_time && <span>{evt.event_time}</span>}
                    {evt.price && <span>{evt.price}</span>}
                    <span className="flex items-center gap-1"><Users size={12} />{evt.registered_count || 0} registered{evt.capacity ? `/${evt.capacity}` : ''}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => viewRegistrations(evt.event_id)} className="btn-outline text-sm flex items-center gap-1" data-testid={`view-registrations-${i}`}><Eye size={14} />Registrations</Button>
                  <Button onClick={() => handleDelete(evt.event_id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`delete-event-${i}`}><Trash2 size={16} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Create Event</h3>
              <form onSubmit={handleCreate} className="space-y-3">
                <Input placeholder="Event Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required data-testid="event-title-input" />
                <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required />
                <Input placeholder="Short Description" value={form.short_description} onChange={(e) => setForm({...form, short_description: e.target.value})} required />
                <select value={form.event_type} onChange={(e) => setForm({...form, event_type: e.target.value})} className="form-input w-full">
                  <option value="webinar">Webinar</option>
                  <option value="workshop">Workshop</option>
                  <option value="networking">Networking</option>
                  <option value="conference">Conference</option>
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <Input type="date" value={form.event_date} onChange={(e) => setForm({...form, event_date: e.target.value})} required />
                  <Input placeholder="Time (e.g. 7:00 PM)" value={form.event_time} onChange={(e) => setForm({...form, event_time: e.target.value})} />
                </div>
                <Input placeholder="Location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_virtual} onChange={(e) => setForm({...form, is_virtual: e.target.checked})} />
                  Virtual Event
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Capacity" type="number" value={form.capacity} onChange={(e) => setForm({...form, capacity: e.target.value})} />
                  <Input placeholder="Price" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" onClick={() => setShowCreate(false)} className="btn-outline flex-1">Cancel</Button>
                  <Button type="submit" className="btn-primary-blue flex-1">Create</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Registrations Modal */}
        {showRegistrations && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#0A0F1C]">Registrations ({registrations.length})</h3>
                <button onClick={() => setShowRegistrations(null)} className="text-[#475569]">&times;</button>
              </div>
              {registrations.length > 0 ? (
                <div className="space-y-3">
                  {registrations.map((r, i) => (
                    <div key={r.registration_id} className="p-3 border border-[#E2E8F0] rounded" data-testid={`registration-${i}`}>
                      <div className="font-medium text-[#0A0F1C]">{r.name}</div>
                      <div className="text-sm text-[#475569]">{r.email}</div>
                      <div className="text-xs text-[#94A3B8]">Registered: {new Date(r.registered_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[#475569] text-center py-4">No registrations yet</p>}
            </div>
          </div>
        )}
      </div></AdminLayout>);}
